import { useEffect, useState } from "react";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, fetchData } from "../../../services";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorMakeStore";
import { useLoading } from "../../../components/Loading/index";
import { useSnackbar } from "../../../context/popup";
import BulkUpload from "../../../components/blukUplod";
import MotorMakeListFilter from "./components/MotorMakeListFilter.js";

const MotorMake = ({tabState}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage = useSelector(
    (state) => state?.motorMakeStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorMakeStore?.rowsPerPage
  );
  const totalPages = useSelector((state) => state?.motorMakeStore?.totalPages);

  const [isRefresh, setIsRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [makeData, setMakeData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
  });

  useEffect(() => {
    if(tabState === "make"){
      fetchMakeData();
    }
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const fetchMakeData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/mmv/makes?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;
      
      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);
      const successResponse = response?.success;
      if (successResponse) {
        setMakeData(response?.data?.items);
      } else {
        showSnackbar("Failed to fetch data", "error");
      }
      dispatch(setTotalPages(response?.data?.totalPages));
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/motor/mmv/make-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar("Motor Make deleted successfully", "success");
      } else {
        showSnackbar("Failed to delete Motor Make", "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar("Failed to delete Motor Make", "error");
    }
  };

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Make Name",
      renderCell: (row) => row.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => new Date(row.createdAt).toLocaleDateString("en-GB"),
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = makeData.map((make, index) => ({
    ...make,
    sno: index + 1,
  }));

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewMake = () => {
    navigate("/motor-master/add-make");
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-make/${row?.id}`, { state: { isEdit: true } });
  };

  const handleView = (row) => {
    const viewMakeData = makeData.find((item) => item?.id === row?.id);
    navigate(`/motor-master/view-make/${row?.id}`, {
      state: { ...viewMakeData, isView: true },
    });
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <div>
      {/* <DashboardLayout> */}
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Make management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of motor make that you can get details of motor make.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewMake()}
              >
                Add Make
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No Motor Make data found"}
          handleSearch={handleSearch}
        />
      </Card>

      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/motor-make"
        fetchList={fetchMakeData}
      />
      <MotorMakeListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchMakeData(); // call API after filter applied
        }}
      />
    </div>
  );
};

export default MotorMake;
